function buildProductContext(productRuleHelpers) {
     if (!productRuleHelpers) {
          return {
               languageAndChannelActive: false,
               language: null,
               channel: null,
               activeProductRuleCount: 0,
               hasAnyAdditionalProductRule: false,
          };
     }

    const {
         LanguageAndChannelHelper,
         StockHelper,
         ProductAttributeHelper,
         RankingAndFilterHelper,
         ProductCategoryHelper,
         ProductRuleHelper
    } = productRuleHelpers;

     const languageAndChannelActive = LanguageAndChannelHelper?.isLanguageAndChannelActive() || false;
     const language = LanguageAndChannelHelper?.getLanguage() || null;
     const channel = LanguageAndChannelHelper?.getChannel() || null;

    const activeRules = ProductRuleHelper?.getActiveRules?.() || [];
    const activeProductRuleCount = Array.isArray(activeRules) ? activeRules.length : 0;

    const stockRuleIds = (Array.isArray(activeRules) ? activeRules : [])
         .filter((rule) => rule?.keyString === "stock")
         .map((rule) => rule.id);

    const hasAnyAdditionalProductRule =
         (ProductAttributeHelper?.isConfigured?.() && ProductAttributeHelper?.isProductAttributeActive?.()) ||
         (RankingAndFilterHelper?.isConfigured?.() && RankingAndFilterHelper?.isRankingAndFilterActive?.()) ||
         (ProductCategoryHelper?.isConfigured?.() && ProductCategoryHelper?.isProductCategoryActive?.()) ||
         StockHelper?.hasConfiguredStockRules?.() ||
         activeProductRuleCount > 1;

     return {
          languageAndChannelActive,
          language,
          channel,
          activeProductRuleCount,
          hasAnyAdditionalProductRule,
          stockRuleIds,
     };
}

function resolveStockReferenceId(stockRuleReference) {
     if (!stockRuleReference) return null;
     if (typeof stockRuleReference === "object") {
          return stockRuleReference.id || null;
     }
     return stockRuleReference;
}

function buildForeignShopContext(foreignShopRuleHelpers) {
     if (!foreignShopRuleHelpers) {
          return {
               hasPriceTracking: false,
               hasForeignShopStockRules: false,
               hasSelectedShops: false,
          };
     }

     const {
          PriceTrackingHelper,
          ForeignShopStockHelper,
          ForeignShopRuleHelper
     } = foreignShopRuleHelpers;

     const hasPriceTracking = !!PriceTrackingHelper?.isActive?.() || false;
     const hasForeignShopStockRules = !!ForeignShopStockHelper?.hasActiveForeignShopStockRules?.() || false;
     const shopKeysWithPrio = PriceTrackingHelper?.getShopKeysWithPrio?.() || {};
     const hasSelectedShops = Object.keys(shopKeysWithPrio).length > 0;

     const activeRules = ForeignShopRuleHelper?.getActiveRules?.() || [];
     const hasAnyForeignShopRule = activeRules.length > 0;

     const foreignShopStockRules = (Array.isArray(activeRules) ? activeRules : [])
          .filter((rule) => rule?.keyString === "foreignShopstock")
          .map((rule) => ({
               id: rule.id,
               stockReferenceId: resolveStockReferenceId(rule.stockRuleReference),
          }));

     const foreignShopStockRuleIds = foreignShopStockRules.map((r) => r.id);

     return {
          hasPriceTracking,
          hasForeignShopStockRules,
          hasSelectedShops,
          hasAnyForeignShopRule,
          foreignShopStockRules,
          foreignShopStockRuleIds,
     };
}

function buildActionContext(actionRuleHelpers) {
     if (!actionRuleHelpers) {
          return {
               hasPriceChangeMargin: false,
               hasCalculateByChannel: false,
               hasRoundPrice: false,
               hasChangeInterval: false,
               hasPriceChange: false,
               hasMaxPriceChange: false,
               hasAmazonBuyBox: false,
               hasAnyActionRule: false,
               calculateByChannelId: null,
          };
     }

     const {
          PriceChangeMarginHelper,
          CalculationChannelAndRuleHelper,
          RoundPriceHelper,
          ChangeIntervalHelper,
          PriceChangeHelper,
          MaxPriceChangeHelper,
          AmazonBuyBoxHelper,
          ActionRuleHelper
     } = actionRuleHelpers;

     const hasPriceChangeMargin = !!PriceChangeMarginHelper?.isActive?.() || false;
     const hasCalculateByChannel = !!CalculationChannelAndRuleHelper?.isActive?.() || false;
     const calculateByChannelId = hasCalculateByChannel
          ? (CalculationChannelAndRuleHelper?.getChannelId?.() ?? null)
          : null;
     const hasRoundPrice = !!RoundPriceHelper?.isActive?.() || false;
     const hasChangeInterval = !!ChangeIntervalHelper?.isActive?.() || false;
     const hasPriceChange = !!PriceChangeHelper?.hasActivePriceAdjustmentRules?.() || false;
     const hasMaxPriceChange = !!MaxPriceChangeHelper?.isActive?.() || false;
     const hasAmazonBuyBox = !!AmazonBuyBoxHelper?.isActive?.() || false;

     const activeRules = ActionRuleHelper?.getActiveRules?.() || [];
     const hasAnyActionRule = activeRules.length > 0;

     const priceChangeRules = (Array.isArray(activeRules) ? activeRules : [])
          .filter((rule) => rule?.keyString === "priceChange")
          .map((rule) => ({
               id: rule.id,
               foreignShopStockRuleReference: rule.foreignShopStockRuleReference || null,
          }));

     return {
          hasPriceChangeMargin,
          hasCalculateByChannel,
          calculateByChannelId,
          hasRoundPrice,
          hasChangeInterval,
          hasPriceChange,
          hasMaxPriceChange,
          hasAmazonBuyBox,
          hasAnyActionRule,
          priceChangeRules,
     };
}

function collectContext({ properties, productRuleHelpers, foreignShopRuleHelpers, actionRuleHelpers }) {
     const product = buildProductContext(productRuleHelpers);
     const foreignShop = buildForeignShopContext(foreignShopRuleHelpers);
     const action = buildActionContext(actionRuleHelpers);

     return {
          properties: properties || {},
          product,
          foreignShop,
          action,
     };
}

function validateLanguageAndChannel(context) {
     const violations = [];
     const { product } = context;

     if (!product.languageAndChannelActive || !product.language || !product.channel) {
          violations.push({
               id: "LANG_AND_CHANNEL_REQUIRED",
               level: "error",
               scope: "product",
               blocking: true,
               message: "Die Komponente \"Sprache & Kanal\" muss aktiv sein und eine Sprache sowie einen Kanal enthalten.",
          });
     }

     return violations;
}

function validateProperties(context) {
     const violations = [];
     const { properties } = context;

     const rawName = properties && typeof properties.name === "string" ? properties.name : "";
     const name = rawName.trim();
     const priority = typeof properties?.priority === "number" ? properties.priority : 0;
     const group = properties?.group;

     if (!name) {
          violations.push({
               id: "NAME_REQUIRED",
               level: "error",
               scope: "properties",
               blocking: true,
               message: "Die Regel muss einen Namen haben.",
          });
     }

     if (!properties?.isBackupRule && (!priority || priority <= 0)) {
          violations.push({
               id: "PRIORITY_REQUIRED",
               level: "error",
               scope: "properties",
               blocking: true,
               message: "Es muss eine Priorität für die Regel gewählt werden.",
          });
     }

     if (!group?.id) {
          violations.push({
               id: "GROUP_REQUIRED",
               level: "error",
               scope: "properties",
               blocking: true,
               message: "Bitte wählen Sie eine Gruppe aus, bevor Sie die Regel speichern.",
          });
     }

     return violations;
}

function validateMinimalComponents(context) {
     const violations = [];
     const { product, foreignShop, action } = context;

     if (product.languageAndChannelActive) {
          const hasAnyOther =
               product.hasAnyAdditionalProductRule ||
               foreignShop.hasAnyForeignShopRule ||
               action.hasAnyActionRule;

          if (!hasAnyOther) {
               violations.push({
                    id: "RULE_TOO_SIMPLE_NO_COMPONENTS",
                    level: "error",
                    scope: "global",
                    blocking: true,
                    message: "Es muss mindestens eine weitere Regel-Komponente neben \"Sprache & Kanal\" aktiviert sein.",
               });
          }
     }

     return violations;
}

function validatePriceTrackingVsMargin(context) {
     const violations = [];
     const { foreignShop, action } = context;

     if (foreignShop.hasPriceTracking && action.hasPriceChangeMargin) {
          violations.push({
               id: "PRICE_TRACKING_WITH_MARGIN",
               level: "error",
               scope: "global",
               blocking: true,
               message: "Preistracking (Fremdshops) und Preisanpassung nach Marge können nicht gleichzeitig aktiviert werden.",
          });
     }

     return violations;
}

function validatePriceChangeVsMargin(context) {
     const violations = [];
     const { action } = context;

     if (action.hasPriceChange && action.hasPriceChangeMargin) {
          violations.push({
               id: "PRICE_CHANGE_WITH_MARGIN",
               level: "error",
               scope: "global",
               blocking: true,
               message: "Preisangleichung und Preisanpassung nach Marge können nicht gleichzeitig aktiviert werden.",
          });
     }

     return violations;
}

function validateForeignShopStockLink(context) {
     const violations = [];
     const { product, foreignShop } = context;

     const stockRuleIds = Array.isArray(product.stockRuleIds) ? product.stockRuleIds : [];
     const foreignShopStockRules = Array.isArray(foreignShop.foreignShopStockRules)
          ? foreignShop.foreignShopStockRules
          : [];

     const hasUnlinkedForeignStock = foreignShopStockRules.some(
          (rule) => !rule.stockReferenceId || !stockRuleIds.includes(rule.stockReferenceId)
     );

     if (hasUnlinkedForeignStock) {
          violations.push({
               id: "FOREIGN_STOCK_WITHOUT_OWN_STOCK",
               level: "error",
               scope: "foreignShop",
               blocking: true,
               message: "Jede Fremdshop-Verfügbarkeit muss mit einer Verfügbarkeits-Regel (Eigene Produkte) verknüpft sein.",
          });
     }

     return violations;
}

function validateForeignShopComponentsNeedShops(context) {
     const violations = [];
     const { foreignShop, action } = context;

     const needsSelectedShops =
          foreignShop.hasPriceTracking ||
          foreignShop.hasForeignShopStockRules ||
          action.hasAmazonBuyBox;

     if (!needsSelectedShops || foreignShop.hasSelectedShops) {
          return violations;
     }

     violations.push({
          id: "FOREIGN_SHOP_COMPONENTS_NEED_SHOPS",
          level: "error",
          scope: "foreignShop",
          blocking: true,
          message: "Für Preistracking, Fremdshop-Verfügbarkeit oder Amazon BuyBox muss mindestens ein Fremdshop im Preistracking ausgewählt sein.",
     });

     return violations;
}

function validatePriceChangeLink(context) {
     const violations = [];
     const { foreignShop, action } = context;

     const foreignShopStockRuleIds = Array.isArray(foreignShop.foreignShopStockRuleIds)
          ? foreignShop.foreignShopStockRuleIds
          : [];
     const priceChangeRules = Array.isArray(action.priceChangeRules)
          ? action.priceChangeRules
          : [];

     if (foreignShopStockRuleIds.length === 0 || priceChangeRules.length === 0) {
          return violations;
     }

     const hasUnlinkedPriceChange = priceChangeRules.some(
          (rule) => !rule.foreignShopStockRuleReference ||
               !foreignShopStockRuleIds.includes(rule.foreignShopStockRuleReference)
     );

     if (hasUnlinkedPriceChange) {
          violations.push({
               id: "PRICE_CHANGE_WITHOUT_FOREIGN_STOCK",
               level: "error",
               scope: "action",
               blocking: true,
               message: "Jede Preisangleichung muss mit einer Fremdshop-Verfügbarkeits-Regel verknüpft sein.",
          });
     }

     return violations;
}

function validateAmazonBuyBoxNeedsPriceFallback(context) {
     const violations = [];
     const { action } = context;

     if (action.hasAmazonBuyBox && !action.hasPriceChange && !action.hasPriceChangeMargin) {
          violations.push({
               id: "AMAZON_BUYBOX_NEEDS_PRICE_FALLBACK",
               level: "error",
               scope: "action",
               blocking: true,
               message: "Amazon BuyBox benötigt eine Preisangleichung oder Preisanpassung nach Marge als Fallback, wenn bereits eine BuyBox vorliegt.",
          });
     }

     return violations;
}

function resolveChannelId(channel) {
     if (!channel) return null;
     if (typeof channel === "object") {
          return channel.id ?? channel.value ?? null;
     }
     return channel;
}

function validateCalculateByChannelNotSameChannel(context) {
     const violations = [];
     const { product, action } = context;

     if (!action.hasCalculateByChannel || action.calculateByChannelId == null) {
          return violations;
     }

     const ownChannelId = resolveChannelId(product.channel);
     if (ownChannelId == null) {
          return violations;
     }

     if (String(ownChannelId) === String(action.calculateByChannelId)) {
          violations.push({
               id: "CALCULATE_BY_CHANNEL_SAME_CHANNEL",
               level: "error",
               scope: "action",
               blocking: true,
               message: "Die Komponente \"Kalkulation auf Kanal- oder Regel-Preis\" darf nicht auf denselben Kanal wie die Regel selbst verweisen.",
          });
     }

     return violations;
}

function validateBeforeSave(context) {
     const allViolations = [
          ...validateLanguageAndChannel(context),
          ...validateProperties(context),
          ...validateMinimalComponents(context),
          ...validatePriceTrackingVsMargin(context),
          ...validatePriceChangeVsMargin(context),
          ...validateForeignShopStockLink(context),
          ...validateForeignShopComponentsNeedShops(context),
          ...validatePriceChangeLink(context),
          ...validateAmazonBuyBoxNeedsPriceFallback(context),
          ...validateCalculateByChannelNotSameChannel(context),
     ];

     const blockingErrors = allViolations.filter(v => v.blocking);
     const warnings = allViolations.filter(v => !v.blocking && v.level !== "info");

     return {
          blockingErrors,
          warnings,
          all: allViolations,
     };
}

function canActivatePriceTracking({ actionRuleHelpers }) {
     if (!actionRuleHelpers?.PriceChangeMarginHelper) {
          return {
               allowed: true,
               reason: null,
          };
     }

     const isMarginActive = !!actionRuleHelpers.PriceChangeMarginHelper.isActive?.();

     if (isMarginActive) {
          return {
               allowed: false,
               reason: "Preistracking Mitbewerb kann nicht aktiviert werden, da bereits eine Preisanpassung nach Marge Komponente existiert.",
          };
     }

     return {
          allowed: true,
          reason: null,
     };
}

const RuleConstraintService = {
     collectContext,
     validateBeforeSave,
     canActivatePriceTracking,
};

export default RuleConstraintService;

